import { Portfolio } from "@/types/Portfolio";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useMemo, useState, createElement, ReactElement } from "react";
import { useFirehoseSources } from "./useFirehoseSources";
import { FilterMode } from "@/shared/models/filters";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import {
  FilterModeSelector,
  ClearSelectionsFooter,
} from "@/shared/components/FilterComponents";
import { isPrebuiltWatchlist } from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import { useFilterPanelMenu } from "@/shared/hooks/useFilterPanelMenu";
import {
  groupWatchlistsByCategory,
  NEWSQUAWK_CATEGORIES,
  NewsquawkCategory,
} from "@/pages/PortfolioBuilder/newsquawkCategories";

interface FirehoseFilterPanelOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setActions?: (actions: ReactElement<any>[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pushAction?: (action: ReactElement<any>) => void;
  persistKey?: string;
  portfolios: Portfolio[];
  sources?: string[];
  /** Callback when panel opens - use to close other panels */
  onOpen?: () => void;
  closeSearch?: () => void;
  onSearchOpen?: (callback: () => void) => () => void;
}

export type { FilterMode };

const HIDE_FILTER_MODE_SELECTOR = true; // Temporarily hide filter mode selector for Firehose widgets

// Helper to check if a portfolio is ticker-based (has tickers, no keywords)
const isTickerWatchlist = (p: Portfolio): boolean => {
  return p.tickers.length > 0 && (!p.keywords || p.keywords.length === 0);
};

// Helper to check if a portfolio is keyword-based (has keywords, no tickers)
const isKeywordWatchlist = (p: Portfolio): boolean => {
  return (p.keywords?.length ?? 0) > 0 && p.tickers.length === 0;
};

export const useFirehoseFilterPanel = (options: FirehoseFilterPanelOptions) => {
  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();
  const firehoseSources = useFirehoseSources();
  const [filterMode, setFilterMode] = useState<FilterMode>("OR");

  // Define filter configurations
  const filterConfig = useMemo(() => {
    // Create filter mode selector for the Filter section
    const filterModeFooter = createElement(FilterModeSelector, {
      filterMode,
      onFilterModeChange: setFilterMode,
    });

    // Separate user and prebuilt watchlists
    const userWatchlists = options.portfolios.filter((p) => !isPrebuiltWatchlist(p));
    const prebuiltWatchlists = options.portfolios.filter((p) => isPrebuiltWatchlist(p));

    // Split user watchlists by type
    const userTickerWatchlists = userWatchlists.filter(isTickerWatchlist);
    const userKeywordWatchlists = userWatchlists.filter(isKeywordWatchlist);
    // Mixed watchlists (both tickers and keywords) go in ticker section
    const userMixedWatchlists = userWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Split prebuilt watchlists by type
    const prebuiltTickerWatchlists = prebuiltWatchlists.filter(isTickerWatchlist);
    const prebuiltKeywordWatchlists = prebuiltWatchlists.filter(isKeywordWatchlist);
    const prebuiltMixedWatchlists = prebuiltWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Group prebuilt keyword watchlists by category
    const groupedKeywordWatchlists = groupWatchlistsByCategory(prebuiltKeywordWatchlists);

    // All ticker watchlist options (user + prebuilt)
    const allTickerWatchlists = [
      ...userTickerWatchlists,
      ...userMixedWatchlists,
      ...prebuiltTickerWatchlists,
      ...prebuiltMixedWatchlists,
    ];
    const tickerWatchlistOptions = allTickerWatchlists.map((portfolio) => ({
      id: String(portfolio.id),
      label: portfolio.name,
    }));

    // Create subsections for ticker watchlists
    const tickerSubsections: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];

    // Add user ticker watchlists subsection (always show to allow creating new)
    const userTickerIds = [...userTickerWatchlists, ...userMixedWatchlists].map((p) => String(p.id));
    tickerSubsections.push({
      id: "user-tickers",
      label: "Your Watchlists",
      filterIds: userTickerIds,
      defaultCollapsed: false,
      createNewLink: { url: "/watchlists-filters?create=ticker" },
    });

    // Add Newsquawk ticker watchlists subsection
    const newsquawkTickerIds = [...prebuiltTickerWatchlists, ...prebuiltMixedWatchlists].map(
      (p) => String(p.id)
    );
    if (newsquawkTickerIds.length > 0) {
      tickerSubsections.push({
        id: "newsquawk-tickers",
        label: "Newsquawk Watchlists",
        filterIds: newsquawkTickerIds,
        defaultCollapsed: true,
      });
    }

    // Build keyword watchlist options with category headers for Newsquawk watchlists
    const keywordWatchlistOptions: { id: string; label: string; isHeader?: boolean }[] = [];

    // Add user keyword watchlists first
    for (const portfolio of userKeywordWatchlists) {
      keywordWatchlistOptions.push({
        id: String(portfolio.id),
        label: portfolio.name,
      });
    }

    // Add Newsquawk keyword watchlists grouped by category with headers
    for (const categoryConfig of NEWSQUAWK_CATEGORIES) {
      const categoryWatchlists = groupedKeywordWatchlists[categoryConfig.id as NewsquawkCategory];
      if (categoryWatchlists && categoryWatchlists.length > 0) {
        // Add category header as non-interactive label
        keywordWatchlistOptions.push({
          id: `header-${categoryConfig.id.toLowerCase().replace(/\s+/g, "-")}`,
          label: categoryConfig.label,
          isHeader: true,
        });
        // Add watchlists in this category
        for (const portfolio of categoryWatchlists) {
          keywordWatchlistOptions.push({
            id: String(portfolio.id),
            label: portfolio.name,
          });
        }
      }
    }

    // Create subsections for keyword watchlists (just two: user and Newsquawk)
    const keywordSubsections: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];

    // Add user keywords as first subsection (always show to allow creating new)
    keywordSubsections.push({
      id: "user-keywords",
      label: "Your Watchlists",
      filterIds: userKeywordWatchlists.map((p) => String(p.id)),
      defaultCollapsed: false,
      createNewLink: { url: "/watchlists-filters?create=keyword" },
    });

    // Add all Newsquawk keyword watchlists in one subsection (with category headers inline)
    const allNewsquawkKeywordIds: string[] = [];
    for (const categoryConfig of NEWSQUAWK_CATEGORIES) {
      const categoryWatchlists = groupedKeywordWatchlists[categoryConfig.id as NewsquawkCategory];
      if (categoryWatchlists && categoryWatchlists.length > 0) {
        // Include the header ID
        allNewsquawkKeywordIds.push(`header-${categoryConfig.id.toLowerCase().replace(/\s+/g, "-")}`);
        // Include watchlist IDs
        for (const portfolio of categoryWatchlists) {
          allNewsquawkKeywordIds.push(String(portfolio.id));
        }
      }
    }
    if (allNewsquawkKeywordIds.length > 0) {
      keywordSubsections.push({
        id: "newsquawk-keywords",
        label: "Newsquawk Watchlists",
        filterIds: allNewsquawkKeywordIds,
        defaultCollapsed: true,
      });
    }

    return [
      // Filter sections - Ticker Watchlists
      {
        id: "ticker-watchlists-filter",
        label: "Filter by Ticker Watchlists",
        description: "Filter by stock symbol watchlists",
        filterOptions: tickerWatchlistOptions,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: tickerSubsections.length > 0 ? tickerSubsections : undefined,
        footer: HIDE_FILTER_MODE_SELECTOR ? null : filterModeFooter,
      },
      // Filter sections - Keyword Watchlists
      {
        id: "keyword-watchlists-filter",
        label: "Filter by Keyword Watchlists",
        description: "Filter by keyword watchlists",
        filterOptions: keywordWatchlistOptions,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: keywordSubsections.length > 0 ? keywordSubsections : undefined,
      },
      ...(options.sources?.includes("allStories")
        ? [
            {
              id: "sources-filter",
              label: "Filter by Source",
              filterOptions: firehoseSources.map((source) => ({
                id: String(source.source),
                label: source.label,
              })),
              collapsible: true,
              showCount: true,
              defaultCollapsed: true,
            },
          ]
        : []),
      {
        id: "category-filter",
        label: "Combined watchlists",
        description:
          "Filters with categories only are currently not supported for Firehose widgets",
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        filterOptions:
          categoryQueries.data
            ?.filter(
              (query) => query.portfolioIds?.length || query.searchKeyword
            )
            .map((query) => ({
              id: String(query.id),
              label: query.queryName,
            })) || [],
        subsections: (() => {
          const queries = (categoryQueries.data || []).filter(
            (query) => query.portfolioIds?.length || query.searchKeyword
          );
          const userIds = queries.filter((q) => q.owner_type === "USER").map((q) => String(q.id));
          const nqIds = queries.filter((q) => q.owner_type === "NEWSQUAWK").map((q) => String(q.id));
          const subs: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];
          subs.push({
            id: "user-filters",
            label: "Your Filters",
            filterIds: userIds,
            defaultCollapsed: false,
            createNewLink: { url: "/watchlists-filters?create=advanced" },
          });
          if (nqIds.length > 0) {
            subs.push({
              id: "newsquawk-filters",
              label: "Newsquawk Filters",
              filterIds: nqIds,
              defaultCollapsed: true,
            });
          }
          return subs;
        })(),
      },
    ] as FilterMenuConfig[];
  }, [
    options.portfolios,
    firehoseSources,
    options.sources,
    categoryQueries.data,
    filterMode,
  ]);

  // Create global footer component with clear selections button
  const globalFooter = useMemo(
    () => createElement(ClearSelectionsFooter, {}),
    []
  );

  const filterPanel = useFilterPanelMenu(
    filterConfig,
    options.pushAction,
    options.persistKey,
    undefined,
    globalFooter,
    options.onOpen,
    options.closeSearch,
    options.onSearchOpen
  );

  return {
    ...filterPanel,
    filterMode,
  };
};
